import { DateTime } from 'luxon';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '../../components/button';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Field,
  FormGrid,
  Input,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Stack,
  Table,
  TableWrap,
  TBody,
  TD,
  TDActions,
  TH,
  THead,
  TR,
} from '../../components/ui';
import {
  useMinimumWageRatesQuery,
  useUpsertMinimumWageRateMutation,
} from '../../services/serviceService/__generated__/queries.generated';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import { currentRateIds, wageInput, type WageForm } from './minimumWage';

const EMPTY: WageForm = { state: '', city: '', hourlyRate: '', effectiveFrom: '', source: '' };

/**
 * Minimum wages per state or city (spec 019 D26, W7a), platform owner. Worker
 * price pages warn when a price works out below the local wage; nothing is
 * refused. A new start date adds a row; the same state, city and date
 * replaces it.
 */
export default function AdminMinimumWagePage() {
  const [filter, setFilter] = useState('');
  const query = useMinimumWageRatesQuery({
    variables: { state: /^[A-Za-z]{2}$/.test(filter.trim()) ? filter.trim().toUpperCase() : null },
    fetchPolicy: 'network-only',
  });
  const [save, { loading }] = useUpsertMinimumWageRateMutation({
    refetchQueries: ['MinimumWageRates'],
  });
  const [form, setForm] = useState<WageForm>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const rows = query.data?.minimumWageRates ?? [];
  const current = currentRateIds(rows, DateTime.now().toISODate()!);
  const set = (key: keyof WageForm, value: string) => setForm(f => ({ ...f, [key]: value }));

  const submit = async () => {
    const { input, problem } = wageInput(form);
    if (!input) {
      setError(problem);
      return;
    }
    setError(null);
    try {
      await save({ variables: input });
      toast.success('Wage saved');
      setForm(EMPTY);
    } catch (e) {
      setError(stayErrorMessage(e, 'Not saved'));
    }
  };

  return (
    <PageContainer width="lg" canvas data-testid="admin-minimum-wage">
      <PageHeader
        eyebrow="Admin"
        title="Minimum wages"
        description="Worker price pages compare each price per hour with the wage where the worker's location is. A city wage wins over its state's."
      />
      <Stack $gap={6}>
        <Card>
          <CardHeader title="Add or change a wage" />
          <CardBody>
            <Stack $gap={4}>
              <FormGrid $cols={3}>
                <Field label="State" htmlFor="wage-state">
                  <Input
                    id="wage-state"
                    maxLength={2}
                    placeholder="AZ"
                    value={form.state}
                    onChange={e => set('state', e.target.value)}
                  />
                </Field>
                <Field label="City" htmlFor="wage-city" hint="Leave empty for the whole state">
                  <Input
                    id="wage-city"
                    value={form.city}
                    onChange={e => set('city', e.target.value)}
                  />
                </Field>
                <Field label="Per hour ($)" htmlFor="wage-rate">
                  <Input
                    id="wage-rate"
                    inputMode="decimal"
                    value={form.hourlyRate}
                    onChange={e => set('hourlyRate', e.target.value)}
                  />
                </Field>
                <Field label="Starts on" htmlFor="wage-from">
                  <Input
                    id="wage-from"
                    type="date"
                    value={form.effectiveFrom}
                    onChange={e => set('effectiveFrom', e.target.value)}
                  />
                </Field>
                <Field label="Source" htmlFor="wage-source" hint='For example "US DOL 2026-01"'>
                  <Input
                    id="wage-source"
                    maxLength={255}
                    value={form.source}
                    onChange={e => set('source', e.target.value)}
                  />
                </Field>
              </FormGrid>
              {error && (
                <Alert tone="error" title="Not saved">
                  {error}
                </Alert>
              )}
              <div>
                <Button
                  type="button"
                  text="Save wage"
                  onClick={submit}
                  isLoading={loading}
                  dataTestId="wage-save"
                />
              </div>
            </Stack>
          </CardBody>
        </Card>
        <Card>
          <CardHeader
            title="Wages"
            actions={
              <Input
                aria-label="Filter by state"
                placeholder="State"
                maxLength={2}
                style={{ width: 90 }}
                value={filter}
                onChange={e => setFilter(e.target.value)}
              />
            }
          />
          {query.loading && !query.data ? (
            <LoadingBlock label="Loading" />
          ) : query.error && !query.data ? (
            <CardBody>
              <Alert tone="error" title="Could not load">
                {stayErrorMessage(query.error)}
              </Alert>
            </CardBody>
          ) : rows.length === 0 ? (
            <CardBody>
              <Muted $size="sm">No wages yet. Price pages then show no wage warning.</Muted>
            </CardBody>
          ) : (
            <TableWrap>
              <Table data-testid="wage-table">
                <THead>
                  <TR>
                    <TH>State</TH>
                    <TH>City</TH>
                    <TH $align="right">Per hour</TH>
                    <TH>Starts</TH>
                    <TH>Source</TH>
                    <TH />
                  </TR>
                </THead>
                <TBody>
                  {rows.map(row => (
                    <TR key={row.id}>
                      <TD>{row.state}</TD>
                      <TD>{row.city ?? 'Whole state'}</TD>
                      <TD $align="right">${row.hourlyRate.toFixed(2)}</TD>
                      <TD>
                        {row.effectiveFrom}{' '}
                        {current.has(row.id) && <Badge tone="success">Now</Badge>}
                      </TD>
                      <TD>{row.source}</TD>
                      <TDActions>
                        <Button
                          type="button"
                          theme="text"
                          size="sm"
                          text="Edit"
                          onClick={() =>
                            setForm({
                              state: row.state,
                              city: row.city ?? '',
                              hourlyRate: String(row.hourlyRate),
                              effectiveFrom: row.effectiveFrom,
                              source: row.source,
                            })
                          }
                        />
                      </TDActions>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          )}
        </Card>
      </Stack>
    </PageContainer>
  );
}
